import useSWR from "swr";
import { useMemo } from "react";

import { BulkUpload } from "../../domain/BulkUpload";
import { useServicesContext } from "../../providers/ServicesProvider";
import { BULK_UPLOAD_STATUS, BULK_UPLOAD_POLL_INTERVAL, SWR_KEY_PREFIX } from "../../constants/constants";

/**
 * Fetches a single bulk user CSV upload by file identifier, for the Uploads tab detail sidebar.
 *
 * Named to avoid colliding with `useBulkUpload`, which performs the CSV *upload* itself.
 *
 * The detail endpoint returns the same shape as the list, so this is not about extra fields — it
 * is about freshness. A row the list fetched minutes ago may since have progressed, so the
 * sidebar re-reads it and, while the upload is still in flight, keeps re-reading it.
 *
 * `fallback` seeds the cache with the row the caller already has, so the sidebar paints
 * immediately and then reconciles against the response instead of showing a skeleton for a record
 * that is already on screen behind it.
 */
export const useBulkUploadDetails = ({
    organizationId,
    fileIdentifier,
    fallback
}: {
    organizationId?: string;
    fileIdentifier?: string;
    fallback?: BulkUpload;
}) => {
    const { userService } = useServicesContext();

    const key = useMemo(() => {
        if (!organizationId || !fileIdentifier) return null;

        // Shares the list's prefix on purpose: `invalidateList` only clears the cached value, so a
        // new CSV import drops an open detail too rather than leaving it stale.
        return `${SWR_KEY_PREFIX.BULK_UPLOADS}:/api/organizations/${organizationId}/users/bulk/uploads/${fileIdentifier}`;
    }, [organizationId, fileIdentifier]);

    const { data: upload, error } = useSWR(
        key,
        () =>
            userService.getBulkUpload({
                organizationId: organizationId ?? "",
                fileIdentifier: fileIdentifier ?? ""
            }),
        {
            fallbackData: fallback,
            // The downstream job mutates this row as it works, so a cached copy goes stale on its
            // own. Same reasoning as `useBulkUploads`.
            revalidateOnMount: true,
            // Only worth polling until the row reaches a terminal status; past that it never
            // changes again and an interval would just generate traffic. Takes the callback form
            // so the decision is re-made against each response — the value form would have to
            // close over `data` from this very call.
            refreshInterval: (latestData?: BulkUpload) => (isInFlight(latestData) ? BULK_UPLOAD_POLL_INTERVAL : 0)
        }
    );

    return {
        error,
        // Derived rather than `isValidating` because SWR v1 has no `isLoading`: a poll sets
        // `isValidating` on every tick, which would flash the skeleton over a populated sidebar
        // every few seconds. Matches the pattern in `useUsers`. Gated on `key` so a hook with
        // nothing to fetch reports idle rather than a load that will never finish.
        loading: Boolean(key) && !error && !upload,
        upload
    };
};

/**
 * Whether the job still has work to do on this row.
 *
 * No record at all is not in flight: either the first read has not landed yet, or it failed — a
 * shared link to an upload that no longer exists. There is nothing to re-read, so an interval
 * would only spin. Polling resumes on its own once a record arrives, because the callback is
 * re-evaluated on the render that delivers it.
 *
 * A record whose status is absent *is* treated as in flight: every field on the wire shape is
 * optional, so a row we cannot classify is more likely mid-write than finished, and re-reading it
 * costs one request.
 */
const isInFlight = (upload?: BulkUpload): boolean => {
    if (!upload) return false;

    return (
        !upload.status ||
        upload.status === BULK_UPLOAD_STATUS.UPLOADED ||
        upload.status === BULK_UPLOAD_STATUS.PROCESSING
    );
};
